import { Cats as GeneratedCats, SubCats as GeneratedSubCats } from "../../generated/static-categories";
import { dasherize } from "../../utils/dom";
import { LS_Set, LS_Get } from "../../utils/local-storage";

// Item condition IDs
export const CONDITION = {
	BRAND_NEW: 5,
	LIKE_NEW: 4,
	GOOD: 3,
	GENTLY_USED: 2,
	QUITE_USED: 1,
} as const;

export type ConditionId = (typeof CONDITION)[keyof typeof CONDITION];

export const CONDITIONS = [
	{
		label: "Brand New",
		id: CONDITION.BRAND_NEW,
		description: "",
	},
	{
		label: "Like New",
		id: CONDITION.LIKE_NEW,
		description: "",
	},
	{
		label: "Good",
		id: CONDITION.GOOD,
		description: "",
	},
	{
		label: "Gently Used",
		id: CONDITION.GENTLY_USED,
		description: "",
	},
	{
		label: "Quite Used",
		id: CONDITION.QUITE_USED,
		description: "",
	},
];

export const BRANDS = [{}];

export const COLORS = ["red", "yellow", "green"];

// Category IDs for special handling
export const CATEGORY_ID = {
	VEHICLES: 12,
	ELECTRONICS: 15,
	JOBS: 31,
} as const;

// Subcategory IDs for special handling
export const SUBCATEGORY_ID = {
	/** Retired 2026-10-08 with the other "Other ..." categories; kept for old references. */
	OTHER_VEHICLES: 44,
	/** Classics, Collectors, and SuperCars: where the classic-car feed files since 2026-10-08. */
	CLASSICS: 159,
	ELECTRIC_CARS: 158,
	CARS: 43,
} as const;

export const PROD_VSC = [158, 43, 159, 160, 95, 44, 164, 161, 162, 291, 292];
const PROD_NON_SHIPABLE_CATS = [31]; // Only Jobs remains non-shippable
const PROD_NON_SHIPABLE_SUBCATS = [];
/**
 * Groups and subcategories, derived from generated/static-categories.ts.
 *
 * This file kept its own copy, edited by hand, and it drifted: Home Decor
 * still pointed at group 40 (gone), Music Media was commented out while 4,476
 * listings sat in it, and Misc stayed after it was retired. The filter panel's
 * category dropdown and the order/account labels read it, so they showed
 * groups that did not exist and missed ones that did.
 */
const PROD_Cats: Record<number, CategoryGroup> = Object.fromEntries(
	Object.entries(GeneratedCats).map(([id, c]) => [id, { label: c.label, sortOrder: c.sortOrder, slug: c.slug }]),
);

const PROD_SubCats: Record<number, Subcategory> = Object.fromEntries(
	Object.entries(GeneratedSubCats).map(([id, c]) => [id, { label: c.label, group: c.group, sortOrder: c.sortOrder }]),
);

export const VISIBLE_CATEGORIES = [0, 12,
	// 31,
	24, 15, 23, 16];
export const CATEGORY_NAMES = [
	"All",
	"Vehicles",
	// "Jobs",
	"Jewelry",
	"Electronics",
	"Fashion",
	"Pets",
];

// All environments now use production category IDs
// Categories are loaded dynamically from database via CategoryProvider
// These constants are only used for vehicle and non-shippable category checks
export const VEHICLE_SUBCATS = PROD_VSC;
export const NON_SHIPABLE_CATS = PROD_NON_SHIPABLE_CATS;
export const NON_SHIPABLE_SUBCATS = PROD_NON_SHIPABLE_SUBCATS;
export const Cats = PROD_Cats;
export const SubCats = PROD_SubCats;
/** A top-level category group, keyed by id in `Cats`. */
export interface CategoryGroup {
	label: string;
	sortOrder: number;
	slug: string;
}

/** A subcategory, keyed by id in `SubCats`, pointing back at its group. */
export interface Subcategory {
	label: string;
	group: number;
	sortOrder: number;
}

/** Ids arrive from query strings and feed imports as either, so both are read. */
type CategoryId = number | string;

export const ALL_CATEGORIES = (): string[] => Object.keys(SubCats);

export const CatLabelsFromId = (catId: CategoryId): string => {
	const subcat = SubCats[catId];
	if (!subcat) return "";
	const cat = Cats[subcat.group];
	return cat && `${cat.label} / ${subcat.label}`;
};

export const catSlugById = (id: CategoryId): string | undefined => {
	return Cats[id]?.slug;
};

export const catNameById = (id: CategoryId): string | undefined => {
	return Cats[id]?.label;
};

export const categoryIdBySlug = (slug: string | undefined | null): number => {
	if (!slug) return 0;
	const found = Object.keys(Cats)
		.map((id) => {
			const catSlug = catSlugById(id);
			if (catSlug == slug) return parseInt(id, 10);
		})
		.filter((a) => !!a);
	if (found.length) {
		const id = found[0];
		return id;
	}
	else {
		return 0;
	}
};

export const getConditionLabel = (conditionId: number | null | undefined): string => {
	if (conditionId === null || conditionId === undefined) return "";

	// Handle the actual ID mapping from CONDITIONS array (1-5)
	const condition = CONDITIONS.find(c => c.id === conditionId);
	// Declared `: string`, so an unmatched id must return "" rather than falling off
	// the end as undefined. Feed imports carry ids outside the 1-5 range, and callers
	// that render this directly would have printed "undefined" next to the label.
	return condition ? condition.label : "";
};

export const getCategoryGroupById = (id: CategoryId): CategoryGroup | undefined => {
	return Cats[id];
};

export const getCategoryById = (id: CategoryId): Subcategory | undefined => {
	return SubCats[id];
};
